/**
 * Client-side validation + sanitization helpers.
 *
 * These mirror the server-side rules (see src/backend/) so a visitor gets an
 * instant, friendly message — but the server always re-validates, because
 * client-side checks can never be trusted on their own.
 *
 * Beyond simple format checks, this module deliberately cross-checks the
 * email and phone fields against each other: a very common data-entry mistake
 * is typing an email address into the phone box (or a phone number into the
 * email box), and we want a clear, specific message for that instead of a
 * confusing "invalid phone number".
 */

export interface FieldError {
  field: string;
  message: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
/** Letters, digits, common phone separators and an optional leading +. */
const PHONE_RE = /^[+]?[\d][\d\s().-]*$/;

/**
 * Trim, neutralise markup and control characters, collapse whitespace and cap
 * the length. This is defence-in-depth only — the server sanitises again.
 */
export function sanitize(value: unknown, maxLength = 500): string {
  if (typeof value !== "string") return "";
  let out = value
    .split("")
    .map((ch) => (ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127 ? " " : ch))
    .join("");
  out = out.replace(/[<>]/g, "");
  out = out.replace(/\s+/g, " ");
  return out.trim().slice(0, maxLength);
}

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/** True when the value clearly looks like an email address (contains "@"). */
export function looksLikeEmail(value: string): boolean {
  return value.includes("@");
}

/** True when the value is made up only of digits and phone separators. */
export function looksLikePhone(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== "" && PHONE_RE.test(trimmed);
}

export function isValidPhone(value: string): boolean {
  const trimmed = value.trim();
  if (!looksLikePhone(trimmed)) return false;
  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

/** Validate the email field, catching a phone number typed into it. */
export function checkEmailField(value: string): FieldError | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return { field: "email", message: "Please enter your email address." };
  }
  if (looksLikePhone(trimmed) && !looksLikeEmail(trimmed)) {
    return {
      field: "email",
      message: "That looks like a phone number — please enter your email address instead.",
    };
  }
  if (!isValidEmail(trimmed)) {
    return { field: "email", message: "Please enter a valid email address." };
  }
  return null;
}

/** Validate the phone field, catching an email address typed into it. */
export function checkPhoneField(value: string): FieldError | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return { field: "phone", message: "Please enter your phone number." };
  }
  if (looksLikeEmail(trimmed)) {
    return {
      field: "phone",
      message: "That looks like an email address — please enter your phone number instead.",
    };
  }
  if (!isValidPhone(trimmed)) {
    return { field: "phone", message: "Please enter a valid phone number (8–15 digits)." };
  }
  return null;
}

export interface LeadInput {
  name: string;
  email: string;
  phone: string;
  service?: string;
  requireService?: boolean;
}

/** Returns the first validation problem, or null when everything is fine. */
export function validateLead(input: LeadInput): FieldError | null {
  if (!input.name || input.name.length < 2) {
    return { field: "name", message: "Please enter your full name." };
  }
  const emailError = checkEmailField(input.email);
  if (emailError) return emailError;
  const phoneError = checkPhoneField(input.phone);
  if (phoneError) return phoneError;
  if (input.requireService && !input.service) {
    return { field: "service", message: "Please choose the service you need." };
  }
  return null;
}